<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="form.id">{{ $t('add_permit.edit_permit') }} </h1>
      <h1 v-else>{{ $t('add_permit.new_permit') }} </h1>
    </div>
    <div class="details-component__content content">
      <form class="form fixed-form">

        <table class="details-table no-col-padding edit-table fixed-table label-col-wide-260">

          <tr class="details-table__row" :class="{'details-table__row_error' : errors.has('parkingCardNr') || errors.has('selectedUser')}" v-if="canUseCards()">
            <td class="details-table__col details-table__col_label">
              <label for="parkingCardNr" class="form__label">{{ $t('add_permit.parking_card_id') }}</label>
            </td>
            <td class="details-table__col">
              <input type="text" class="form__input"
                      name="parkingCardNr"
                      id="parkingCardNr"
                      v-model="form.parkingCardNr"
                      v-validate="'validParkingCardNr'"
                      :data-vv-as="$t('add_permit.parking_card_id')"
                      data-vv-delay="1000" ref="parkingCardNr"/>
              <tooltip v-show="errors.has('parkingCardNr')" class="form__validation">{{ $t('errors.valid_card_field') }}</tooltip>
              <tooltip v-show="errors.has('vehicleReg')" class="form__validation">{{ $t('errors.card_user_field_required') }}</tooltip>
            </td>
          </tr>

          <tr v-if="isSuperAdmin()" class="details-table__row" :class="{'details-table__row_error' : errors.has('landlord')}">
            <td class="details-table__col details-table__col_label">
              <label for="landlord" class="form__label">{{ $t('common.landlord')}}</label>
            </td>
            <td class="details-table__col landlord__name">
              <el-select name="landlord"
                         v-model="landlord"
                         v-validate="'validLandlord'"
                         :data-vv-as="$t('common.landlord')"
                         v-if="isNew && landlords.items"
                         :placeholder="$t('common.landlord')"
                         @change="selectedTenant=null">
                <el-option
                  v-for="landlord in landlords.items"
                  :key="landlord.id"
                  :label="landlord.name"
                  :value="landlord.id">
                </el-option>
              </el-select>
              <span v-else-if="permit.partner && permit.partner.landlord" class="disabled">{{ permit.partner.landlord.name }}</span>
              <tooltip v-show="errors.has('landlord')" class="form__validation">{{ $t('errors.landlord_field_required') }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row" v-if="isLandlord()" :class="{'details-table__row_error' : errors.has('selectedTenant')}">
            <td class="details-table__col details-table__col_label">
              <label for="tenant" class="form__label">{{ $t('common.tenant') }}</label>
            </td>
            <td class="details-table__col">
              <el-select name="selectedTenant"
                v-model="selectedTenant"
                v-validate="'selectedTenant'"
                :data-vv-as="$t('common.tenant')"
                v-if="isNew && tenants.items"
                placeholder="">
                <el-option
                  v-for="tenant in tenants.items.filter(t => t.permitPack)"
                  v-if="tenant.id === landlord || (tenant.landlord && tenant.landlord.id === landlord)"
                  :key="tenant.id"
                  :label="tenant.name"
                  :value="tenant.id">
                </el-option>
              </el-select>
              <span v-else-if="permit.partner" class="disabled">{{ permit.partner.name }}</span>
              <tooltip v-show="errors.has('selectedTenant')" class="form__validation">{{ $t('errors.tenant_field_required') }}</tooltip>
            </td>
          </tr>

          <!-- Users -->
          <tr class="details-table__row" v-if="selectedTenant || isTenant()" :class="{'details-table__row_error' : errors.has('selectedUser')}">
            <td class="details-table__col details-table__col_label">
              <label for="user" class="form__label">{{ $t('common.user') }}</label>
            </td>
            <td class="details-table__col">
              <el-select name="selectedUser"
                v-model="selectedUser"
                v-validate="'userOrParkingCardNrRequired'"
                :data-vv-as="$t('common.user')"
                ref="selectedUser"
                clearable
                placeholder="">
                <el-option
                  v-for="user of users.items"
                  :key="user.id"
                  :label="user.name"
                  :value="user.id">
                </el-option>
              </el-select>
              <tooltip v-show="errors.has('selectedUser')" class="form__validation">{{ $t('errors.card_user_field_required') }}</tooltip>
            </td>
          </tr>

          <!-- Vehicles -->
          <tr class="details-table__row" v-if="selectedUser || isTenant()">
            <td class="details-table__col details-table__col_label">
              <label for="user" class="form__label">{{ $tc('common.user_vehicle_reg', 0) }}</label>
            </td>
            <td class="details-table__col">
              <div v-if="selectedVehicle">
                <el-tag type="success" :closable="true" @close="clearVehicle">
                  {{ getVehicleRegById(selectedVehicle) }}
                </el-tag>
              </div>
              <button
                type="button"
                class="buttonset__button btn btn--secondary"
                @click="showVehicleModal = true"
              >
                {{ selectedVehicle ? $t('add_permit.change_plate') : $t('add_permit.add_plate') }}
              </button>
            </td>
          </tr>

          <!-- Products -->
          <tr class="details-table__row" :class="{'details-table__row_error' : errors.has('product')}" v-show="tenantHasProducts">
            <td class="details-table__col details-table__col_label">
              <label for="product" class="form__label">{{ $tc('add_permit.product', 'product.total') }}</label>
            </td>
            <td class="details-table__col">
              <el-select
                name="product"
                v-model="form.productId"
                v-validate="'required'"
                ref="selectedProduct"
                placeholder=""
              >
                <el-option
                  v-for="product of products.items"
                  v-if="canAddPermitTo(product)"
                  :key="product.id"
                  :label="product.name + ' - ' + product.address"
                  :value="product.id"
                >
                </el-option>
              </el-select>
              <tooltip v-show="errors.has('product')" class="form__validation">{{ $t('errors.product_field_required') }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row form__field_datepicker">
            <td class="details-table__col details-table__col_label">
              <label class="form__label">{{ $t('permits.valid_from_date') }}</label>
            </td>
            <td class="details-table__col auto-height">
              {{form.test}}
              <el-date-picker
                v-model="form.validFromDate"
                format="dd.MM.yyyy"
                type="date"
                name="validFromDate"
                :placeholder="defaultDate"
                :default-value="defaultDateValue"
                :picker-options="startTimeOptions"
                :clearable="true"
                value-format="dd.MM.yyyy">
              </el-date-picker>
            </td>
          </tr>

          <tr class="details-table__row form__field_datepicker">
            <td class="details-table__col details-table__col_label">
              <label class="form__label">{{ $t('permits.expiration_date') }}</label>
            </td>
            <td class="details-table__col auto-height">
              <el-date-picker
                v-model="form.expirationDate"
                format="dd.MM.yyyy"
                type="date"
                name="expirationDate"
                :picker-options="expireTimeOptions"
                :clearable="true"
                value-format="dd.MM.yyyy">
              </el-date-picker>
            </td>
          </tr>

        </table>

        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'permits' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'permit', params: { id: $route.params.id} }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
            {{ $t('add_permit.save') }}
          </button>
        </div>
      </form>

      <VehicleSelectionModal
        :show="showVehicleModal"
        :user-id="selectedUser"
        @close="showVehicleModal = false"
        @confirmed="confirmedVehicle"
        v-if="selectedUser"
      />

      <WarningModal v-if="tenantHasNoProducts || showWarningModal" @close="closeWarningModal">
        <h1 slot="header">{{ $t('common.warning') }}</h1>
        <p slot="body">{{ warningBody }}</p>
      </WarningModal>
    </div>
  </div>

</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import VehicleSelectionModal from 'common/VehicleSelectionModal';
import dechex from 'locutus/php/math/dechex';
import hexdec from 'locutus/php/math/hexdec';
import strpad from 'locutus/php/strings/str_pad';

export default {
  components: {
    Tooltip,
    ConfirmationModal,
    WarningModal,
    VehicleSelectionModal,
  },
  watch: {
    selectedTenant: 'fetchTenantData',
    selectedUser: 'fetchVehiclePlateNumbers',
    $route: 'checkView', // ''fetchAllData',
    vehicles: 'preselectVehicle',
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    isUserPermit() {
      return this.$route.params.userId;
    },
    tenantHasNoProducts() {
      return this.isTenant() && this.products.items && this.products.items.length === 0;
    },
    defaultDate() {
      return moment().format('DD.MM.YYYY');
    },
    defaultDateValue() {
      return moment().toDate();
    },
    ...mapGetters({
      landlords: 'landlord/landlords/landlords',
      products: 'landlord/products/products',
      permitError: 'permits/permit/error',
      permit: 'permits/permit/permit',
      tenants: 'landlord/tenants/tenants',
      user: 'user/user/user',
      users: 'landlord/users/users',
      selectedUserData: 'users/user/user',
      vehicles: 'users/vehicles/vehicles',
      permitsLimit: 'landlord/permits/limit',
      permitsKeywordFilter: 'landlord/permits/keywordFilter',
    }),
  },
  data() {
    return {
      form: {},
      landlord: null,
      selectedTenant: null,
      selectedUser: null,
      selectedVehicle: null,
      showVehicleModal: false,
      showConfirmationModal: false,
      showWarningModal: false,
      tenantHasProducts: false,
      startTimeOptions: {
        disabledDate: date => (moment(date).isAfter(moment(this.form.expirationDate, 'DD.MM.YYYY').add(-1, 'day'), 'day')),
        firstDayOfWeek: 1,
      },
      expireTimeOptions: {
        disabledDate: date => (moment(date).isBefore(moment(this.form.validFromDate, 'DD.MM.YYYY').add(1, 'day'), 'day')),
        firstDayOfWeek: 1,
      },
      warningBody: null,
    };
  },
  mounted() {
    this.fetchAllData();
  },
  created() {
    this.checkView();
    this.resetValidators();
    this.createValidators();
  },
  methods: {
    getVehicleRegById(vehicleId) {
        if (!this.vehicles || !this.vehicles.items) return '';
        const vehicle = this.vehicles.items.find(v => v.id === vehicleId);
        return vehicle ? vehicle.vehicleReg : '';
    },
    confirmedVehicle(vehicle) {
        this.selectedVehicle = vehicle.id;
        this.showVehicleModal = false;
    },
    checkView() {
      if (this.isNew) {
        this.$store.commit(`users/user/${mTypes.CLEAR_STATE}`);
        this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
        this.$store.commit(`landlord/users/${mTypes.CLEAR_STATE}`);
        this.$store.commit(`landlord/products/${mTypes.CLEAR_STATE}`);
        this.$store.commit(`users/vehicles/${mTypes.CLEAR_STATE}`);
        this.form = {
          validFromDate: moment().toDate(),
        };
        this.landlord = this.user.data.selectedRole.id;
        this.selectedTenant = null;
        this.selectedUser = null;
        this.selectedVehicle = null;
      }
    },
    hasProducts() {
      if (this.selectedTenant || this.isTenant()) {
        let productsNotFull = false;
        if (this.products.total > 0) {
          this.products.items.forEach((el) => {
            if (el.issued < el.total) {
              productsNotFull = true;
            }
          });
        }
        this.tenantHasProducts = this.products.total > 0 && (
          productsNotFull || this.$route.params.id
        );
      }
    },
    fetchVehiclePlateNumbers() {
      this.selectedVehicle = null;
      if (this.selectedUser) {
        this.$store.dispatch('users/vehicles/fetch', { id: this.selectedUser, user: this.user }).then(() => {
          if (this.vehicles.items.length > 0 && this.permit.vehicle && this.permit.vehicle.id
            && this.vehicles.items.filter(vehicle =>
              vehicle.id === this.permit.vehicle.id,
            ).length > 0) {
            this.selectedVehicle = this.permit.vehicle.id;
          }
        });
      }
    },
    preselectVehicle() {
      if (!this.isNew) {
        return;
      }
      if (this.vehicles.items && this.vehicles.items.length > 0) {
        this.vehicles.items.forEach((el) => {
          if (el.default) {
            this.selectedVehicle = el.id;
          }
        });
        if (!this.selectedVehicle) {
          this.selectedVehicle = this.vehicles.items[0].id;
        }
      }
    },
    fetchTenantData() {
      this.fetchProducts();
      this.fetchUsers();
    },
    fetchUser() {
      // TODO bug here?
      this.$store.dispatch('users/user/fetch', { id: this.$route.params.userId, user: this.user })
        .then(() => {
          this.selectedUser = this.selectedUserData.id;
        });
    },
    fetchUsers() {
      this.selectedUser = null;
      if (this.isTenant()) {
        this.$store.dispatch('landlord/users/fetch', { user: this.user, limit: 200 }).then(() => {
          if (this.permit.user) {
            this.selectedUser = this.permit.user.id;
          }
        });
      } else {
        this.$store.dispatch('landlord/users/fetch', { user: this.user, partnerId: this.selectedTenant, limit: 200 }).then(() => {
          if (this.permit.user) {
            this.selectedUser = this.permit.user.id;
          }
        });
      }
    },
    fetchAllData() {
      if (this.user) {
        if (this.isNew) {
          this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
        } else {
          this.fetchPermit();
        }
        if (this.isUserPermit) {
          this.fetchUser();
        }
        if (this.$route.params.userId) {
          this.selectedUser = this.$route.params.userId;
        }
        this.fetchProducts();
        if (this.isLandlord()) {
          if (this.selectedUser && !this.selectedTenant) {
            this.$store.dispatch('users/user/fetch', { id: this.selectedUser, user: this.user })
              .then(() => {
                this.selectedTenant = this.selectedUserData.partner[0].id;
                this.landlord = this.selectedUserData.partner[0].landlord.id;
              });
          }
          this.fetchTenants();
        } else {
          this.fetchUsers();
        }
        if (this.isSuperAdmin()) {
          this.fetchLandlords();
        }
      }
    },
    fetchProducts() {
      this.productId = null;
      if (this.isLandlord() && this.selectedTenant !== null && this.user.data && typeof this.selectedTenant !== 'undefined') {
        this.$store.dispatch('landlord/products/fetchTenantProducts', { user: this.user, id: this.selectedTenant, type: 'permitPack' }).then(() => {
          this.hasProducts();
        });
      } else if (this.isTenant() && this.user.data) {
        this.$store.dispatch('landlord/products/fetchTenantProducts', { user: this.user, type: 'permitPack' }).then(() => {
          this.hasProducts();
        });
      } else {
        this.products.items = {};
      }
    },
    fetchTenants() {
      if (this.user.data) {
        this.$store.dispatch('landlord/tenants/list', { user: this.user });
      }
    },
    fetchLandlords() {
      if (this.user.data) {
        this.$store.dispatch('landlord/landlords/list', { user: this.user });
      }
    },
    fetchPermit() {
      if (this.user.data) {
        this.$store.dispatch('permits/permit/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
          this.form = this.permit;
          if (this.permit.partner) {
            this.selectedTenant = this.permit.partner.id;
            this.landlord = this.permit.partner.landlord.id;
          }
          if (this.permit.product) {
            this.form.productId = this.permit.product.id;
          }
          if (this.permit.vehicle) {
            this.selectedVehicle = this.permit.vehicle.id;
          }
          if (this.permit.user) {
            this.selectedUser = this.permit.user.id;
          }
        });
      }
    },
    onSubmit() {
      this.$validator.validateAll(
        { selectedUser: 'userOrParkingCardNrRequired',
          parkingCardNr: this.form.parkingCardNr,
          mobile: this.form.mobilePhoneNr,
          email: this.form.email,
          product: this.form.productId,
          selectedTenant: 'selectedTenant' },
      )
        .then((success) => {
          this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, null);
          if (!this.tenantHasProducts) {
            this.warningBody = this.$t('permits.tenant_has_no_products');
            this.showWarningModal = true;
            return;
          }
          if (this.form.validFromDate && this.form.validFromDate !== '') {
            this.form.validFromDate = moment(this.form.validFromDate, 'DD.MM.YYYY').toDate();
          } else {
            this.form.validFromDate = moment().toDate();
          }
          if (this.form.expirationDate && this.form.expirationDate !== '') {
            this.form.expirationDate = moment(this.form.expirationDate, 'DD.MM.YYYY').toDate();
          } else {
            delete this.form.expirationDate;
          }
          if (!this.canUseCards()) {
            this.form.parkingCardNr = null;
          }
          if (success) {
            this.form.tenantId = this.isTenant()
              ? this.user.data.selectedRole.id
              : this.selectedTenant;
            this.form.userId = this.selectedUser;
            this.form.vehicleId = this.selectedVehicle;

            // Fix strange date problem
            const clonedForm = JSON.parse(JSON.stringify(this.form));
            clonedForm.validFromDate = moment(this.form.validFromDate).format();
            if (clonedForm.expirationDate) {
              clonedForm.expirationDate = moment(this.form.expirationDate).format();
            }
            this.$store.dispatch('permits/permit/savePermit', { permit: clonedForm, user: this.user })
              .then(() => {
                if (this.permitError) {
                  this.warningBody = this.$t(`errors.${ this.permitError.body.message }`);
                  this.showWarningModal = true;
                }
                if (!this.permitError) {
                  this.form = {};
                  this.resetValidators();
                  this.$store.dispatch('landlord/permits/fetch', { user: this.user, limit: this.permitsLimit, keyword: this.permitsKeywordFilter });
                  this.$router.push({ name: 'permit', params: { id: this.permit.id } });
                }
              })
              .catch(() => {
                this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, true);
              });
          } else {
            this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, true);
          }
        })
        .catch(() => {
          this.$store.commit(`products/product/${mTypes.DATA_ERROR}`, true);
        });
    },
    resetValidators() {
      this.$validator.remove('validParkingCardNr');
      this.$validator.remove('userOrParkingCardNrRequired');
      this.$validator.remove('selectedTenant');
      this.$validator.remove('validLandlord');
      this.$validator.clean();
    },
    createValidators() {
      this.createParkingCardValidator();
      this.createUserOrParkingCardRequiredValidator();
      this.createTenantValidator();
      this.createLandlordValidator();
    },
    createParkingCardValidator() {
      this.$validator.extend('validParkingCardNr', {
        getMessage: field => `The ${field} value is not valid parking card number.`,
        validate: (value) => {
          if (value === '') {
            return true;
          }
          // check if card number is 9 characters long and contains only hex characters
          return /^[0-9A-F]{9}$/i.test(value);
        },
      });
    },
    createUserOrParkingCardRequiredValidator() {
      this.$validator.extend('userOrParkingCardNrRequired', {
        getMessage: () => 'Parking card number or user must be set',
        validate: () => {
          const parking = this.$refs.parkingCardNr ? this.$refs.parkingCardNr.value : null;
          const user = this.$refs.selectedUser ? this.$refs.selectedUser.value : null;
          return !((!parking || parking === '') && (!user || user === ''));
        },
      });
    },
    createTenantValidator() {
      this.$validator.extend('selectedTenant', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isLandlord() && (typeof this.selectedTenant !== 'undefined' && this.selectedTenant !== null),
      });
    },
    createLandlordValidator() {
      this.$validator.extend('validLandlord', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isSuperAdmin() && (typeof this.landlord !== 'undefined' && this.landlord !== null),
      });
    },
    closeWarningModal() {
      this.showWarningModal = false;
      if (this.isTenant()) {
        this.$router.push({ name: 'dashboard' });
      }
    },
    clearUser() {
      this.selectedUser = null;
    },
    clearVehicle() {
      this.selectedVehicle = null;
    },
    canAddPermitTo(product) {
      return product.issued < product.total || product.canExceedCapacity ||
        (this.permit.product && product.id === this.permit.product.id);
    },
    canUseCards() {
      if (this.form.productId != null) {
        for (let i = 0, l = this.products.items.length; i < l; i += 1) {
          if (this.products.items[i].id === this.form.productId) {
            return this.products.items[i].canUseCards;
          }
        }
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/details.scss';

.form {
  &__selectbox {
    width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/permits/AddEditPermitView.vue